import React, { useState, useEffect } from 'react';
import { Helmet } from 'react-helmet';
import Header from '../../components/ui/Header';
import Icon from '../../components/AppIcon';
import Input from '../../components/ui/Input';
import Button from '../../components/ui/Button';
import UniversityAnalysisModal from '../university-directory/components/UniversityAnalysisModal';
import AnalysisHistory from '../university-directory/components/AnalysisHistory';
import AnalysisResultsModal from '../university-directory/components/AnalysisResultsModal';
import { supabase } from '../../lib/supabase';
import { useAuth } from '../../contexts/AuthContext';
import { API_ENDPOINTS } from '../../config';

const UniversityAnalyse = () => {
  const { user } = useAuth();
  const [searchQuery, setSearchQuery] = useState('');
  const [universities, setUniversities] = useState([]);
  const [filteredUniversities, setFilteredUniversities] = useState([]);
  const [selectedUniversity, setSelectedUniversity] = useState(null);
  const [loading, setLoading] = useState(false);
  const [searchOpen, setSearchOpen] = useState(false);
  const [analysisKey, setAnalysisKey] = useState(0);
  const [viewingAnalysis, setViewingAnalysis] = useState(null);
  const [resultsModalOpen, setResultsModalOpen] = useState(false);

  // Fetch universities on mount
  useEffect(() => {
    const fetchUniversities = async () => {
      try {
        setLoading(true);
        const { data, error: fetchError } = await supabase
          .from('universities')
          .select('id, name, country, admit_rate, gpa, sat')
          .order('name');

        if (fetchError) throw fetchError;
        setUniversities(data || []);
      } catch (err) {
        console.error('Error fetching universities:', err);
        setUniversities([]);
      } finally {
        setLoading(false);
      }
    };

    fetchUniversities();
  }, []);

  // Filter universities based on search query
  useEffect(() => {
    if (searchQuery.length > 0) {
      const filtered = universities.filter(uni =>
        uni.name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
        uni.country?.toLowerCase().includes(searchQuery.toLowerCase())
      );
      setFilteredUniversities(filtered.slice(0, 10));
      setSearchOpen(true);
    } else {
      setFilteredUniversities([]);
      setSearchOpen(false);
    }
  }, [searchQuery, universities]);

  const handleSelectUniversity = (university) => {
    setSelectedUniversity(university);
    setSearchQuery(university.name);
    setSearchOpen(false);
  };

  const handleAnalysisComplete = (analysisData) => {
    setSearchQuery('');
    setSelectedUniversity(null);
    setAnalysisKey(prev => prev + 1);
    // Show results immediately after analysis completes
    if (analysisData) {
      setViewingAnalysis(analysisData);
      setResultsModalOpen(true);
    }
  };

  const handleViewAnalysis = async (analysisId) => {
    try {
      const response = await fetch(API_ENDPOINTS.getAnalysis(analysisId));
      const data = await response.json();
      
      if (data.success && data.data) {
        setViewingAnalysis(data.data);
        setResultsModalOpen(true);
      }
    } catch (err) {
      console.error('Error fetching analysis:', err);
    }
  };

  return (
    <>
      <Helmet>
        <title>Analyse Your Chances - UniSteps</title>
        <meta
          name="description"
          content="Get AI-powered insights on your admission chances to any university. Analyse is powered by advanced AI and your complete student profile."
        />
      </Helmet>
      <div className="min-h-screen app-shell">
        <Header />

        <main className="pt-20 sm:pt-24 pb-20 sm:pb-14 safe-bottom mobile-scroll">
          <div className="max-w-4xl mx-auto px-3 sm:px-4 lg:px-8 space-y-8 sm:space-y-12">
            {/* Hero Section */}
            <div className="surface-card rounded-xl sm:rounded-2xl p-4 sm:p-6 lg:p-8 overflow-hidden relative lifted">
              <div className="absolute inset-0 tile-grid opacity-60" aria-hidden="true" />
              <div className="absolute inset-0 aurora-blob opacity-45" aria-hidden="true" />
              <div className="relative z-10">
                <div className="flex items-center gap-2 sm:gap-3 mb-3 sm:mb-4">
                  <Icon name="Zap" size={24} className="sm:w-8 sm:h-8 text-primary" />
                  <h1 className="text-2xl sm:text-3xl md:text-4xl font-bold text-foreground headline-serif">
                    Analyse Your Admission Chances
                  </h1>
                </div>
                <p className="text-sm sm:text-base lg:text-lg text-muted-foreground max-w-3xl">
                  Get AI-powered insights on your realistic admission probability. Our advanced AI analyzes your complete portfolio and compares it to university requirements.
                </p>
              </div>
            </div>

            {/* Search Section */}
            <div className="bg-card border border-border rounded-xl sm:rounded-2xl p-4 sm:p-6 lg:p-8">
              <h2 className="text-xl sm:text-2xl font-bold text-foreground mb-4 sm:mb-6 flex items-center gap-2">
                <Icon name="Search" size={20} className="sm:w-6 sm:h-6" />
                Find a University
              </h2>

              <div className="relative">
                <div className="relative">
                  <Input
                    type="text"
                    placeholder="Search by university name, location, or country..."
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    onFocus={() => searchQuery.length > 0 && setSearchOpen(true)}
                    iconName="Search"
                    variant="filled"
                  />
                </div>

                {/* Dropdown Results */}
                {searchOpen && filteredUniversities.length > 0 && (
                  <div className="search-dropdown absolute top-full left-0 right-0 mt-2 bg-card border border-border rounded-xl shadow-lg z-50 overflow-hidden max-h-[60vh] overflow-y-auto mobile-scroll">
                    {filteredUniversities.map((uni) => (
                      <button
                        key={uni.id}
                        onClick={() => handleSelectUniversity(uni)}
                        className="w-full text-left px-3 sm:px-4 py-3 hover:bg-muted active:bg-muted transition-all border-b border-border/50 last:border-0 flex items-start justify-between touch-feedback"
                      >
                        <div className="min-w-0 flex-1">
                          <div className="font-semibold text-sm sm:text-base text-foreground truncate">{uni.name}</div>
                          <div className="text-xs sm:text-sm text-muted-foreground truncate">{uni.country}</div>
                        </div>
                        <div className="text-xs sm:text-sm font-medium text-primary whitespace-nowrap ml-3 flex-shrink-0">
                          {uni.admit_rate}%
                        </div>
                      </button>
                    ))}
                  </div>
                )}

                {searchOpen && searchQuery.length > 0 && filteredUniversities.length === 0 && (
                  <div className="absolute top-full left-0 right-0 mt-2 bg-card border border-border rounded-xl shadow-lg z-50 p-4 text-center">
                    <Icon name="Search" size={24} className="text-muted-foreground mx-auto mb-2" />
                    <p className="text-muted-foreground text-xs sm:text-sm">No universities found matching "{searchQuery}"</p>
                  </div>
                )}
              </div>

              {/* Selected University Display */}
              {selectedUniversity && (
                <div className="mt-4 sm:mt-6 p-3 sm:p-4 bg-primary/10 border border-primary/20 rounded-lg">
                  <div className="flex items-start justify-between mb-3 sm:mb-4 gap-2">
                    <div className="min-w-0 flex-1">
                      <h3 className="font-bold text-foreground text-base sm:text-lg truncate">{selectedUniversity.name}</h3>
                      <p className="text-xs sm:text-sm text-muted-foreground truncate">{selectedUniversity.country}</p>
                    </div>
                    <button
                      onClick={() => {
                        setSelectedUniversity(null);
                        setSearchQuery('');
                      }}
                      className="p-2 hover:bg-primary/20 active:bg-primary/30 rounded-lg transition-all touch-feedback flex-shrink-0"
                    >
                      <Icon name="X" size={18} className="sm:w-5 sm:h-5" />
                    </button>
                  </div>

                  <div className="grid grid-cols-3 gap-2 sm:gap-3 mb-3 sm:mb-4">
                    <div>
                      <div className="text-[10px] sm:text-xs text-muted-foreground mb-1">Admit Rate</div>
                      <div className="text-base sm:text-lg font-bold text-primary">
                        {selectedUniversity.admit_rate}%
                      </div>
                    </div>
                    <div>
                      <div className="text-xs text-muted-foreground mb-1">Avg GPA</div>
                      <div className="text-lg font-bold text-foreground">
                        {selectedUniversity.gpa || 'N/A'}
                      </div>
                    </div>
                    <div>
                      <div className="text-xs text-muted-foreground mb-1">SAT Range</div>
                      <div className="text-sm font-bold text-foreground">
                        {selectedUniversity.sat || 'N/A'}
                      </div>
                    </div>
                  </div>

                  {user?.id && (
                    <UniversityAnalysisModal
                      isOpen={true}
                      onClose={() => {
                        setSelectedUniversity(null);
                        setSearchQuery('');
                      }}
                      userId={user.id}
                      currentUniversityId={selectedUniversity.id}
                      onAnalysisComplete={handleAnalysisComplete}
                    />
                  )}

                  {!user?.id && (
                    <div className="p-4 bg-amber-500/10 border border-amber-500/20 rounded-lg text-center">
                      <p className="text-sm text-amber-800 dark:text-amber-200">
                        Please log in to analyse your admission chances
                      </p>
                    </div>
                  )}
                </div>
              )}

              {!selectedUniversity && (
                <div className="mt-8 text-center">
                  <Icon name="Search" size={48} className="text-muted-foreground/30 mx-auto mb-3" />
                  <p className="text-muted-foreground">
                    Start by searching for a university above to analyze your admission chances
                  </p>
                </div>
              )}
            </div>

            {/* Analysis History Section */}
            {user?.id && (
              <div className="bg-card border border-border rounded-2xl p-6 lg:p-8">
                <h2 className="text-2xl font-bold text-foreground mb-6 flex items-center gap-2">
                  <Icon name="History" size={24} />
                  My Analyses
                </h2>
                <AnalysisHistory 
                  key={analysisKey}
                  userId={user.id} 
                  onViewAnalysis={handleViewAnalysis} 
                />
              </div>
            )}

            {!user?.id && (
              <div className="bg-card border border-border rounded-2xl p-8 text-center">
                <Icon name="LogIn" size={48} className="text-muted-foreground/30 mx-auto mb-4" />
                <h3 className="text-lg font-semibold text-foreground mb-2">Log in to see your analyses</h3>
                <p className="text-muted-foreground mb-4">
                  All your admission analyses will be saved here for future reference
                </p>
              </div>
            )}
          </div>
        </main>

        {/* Results Modal */}
        <AnalysisResultsModal 
          isOpen={resultsModalOpen}
          onClose={() => {
            setResultsModalOpen(false);
            setViewingAnalysis(null);
          }}
          analysis={viewingAnalysis}
        />
      </div>
    </>
  );
};

export default UniversityAnalyse;
